import { useEffect, useState } from 'react';
import type { Feed } from '../types';

export type FeedSource = 'production' | 'fixture';

type FeedState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ok'; feed: Feed; source: FeedSource };

/**
 * Fetches a dashboard feed (analyses.json shape).
 *
 * @param primaryUrl   Production URL to try first (e.g. ./data/analyses.json).
 * @param fallbackUrl  Sample fixture URL used when primary returns 404/error.
 */
export function useFeed(primaryUrl: string, fallbackUrl?: string): FeedState {
  const [state, setState] = useState<FeedState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    async function load() {
      const urls: Array<{ url: string; source: FeedSource }> = [
        { url: primaryUrl, source: 'production' },
        ...(fallbackUrl ? [{ url: fallbackUrl, source: 'fixture' as FeedSource }] : []),
      ];

      let lastError = '';
      for (const { url, source } of urls) {
        try {
          const r = await fetch(url);
          if (!r.ok) {
            lastError = `HTTP ${r.status} ${r.statusText} (${url})`;
            continue; // try fallback
          }
          const text = await r.text();
          if (text.trimStart().startsWith('<')) {
            lastError = `Expected JSON, got HTML (${url})`;
            continue;
          }
          const feed = JSON.parse(text) as Feed;
          if (!cancelled) setState({ status: 'ok', feed, source });
          return;
        } catch (err) {
          lastError = String(err);
        }
      }
      if (!cancelled) setState({ status: 'error', message: lastError });
    }

    load();
    return () => { cancelled = true; };
  }, [primaryUrl, fallbackUrl]);

  return state;
}
